import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, ResponsiveContainer } from 'recharts';
import { AlertTriangle, Users, Sparkles, Save, Loader2, TrendingUp, TrendingDown, AlertCircle } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import UserManualTable from './UserManualTable';

const DIMENSIONS = [
{ key: 'intensity', label: 'Intensity Tolerance' },
{ key: 'ownership', label: 'Ownership Bias' },
{ key: 'speed', label: 'Speed vs Polish' },
{ key: 'directness', label: 'Directness' },
{ key: 'adaptability', label: 'Adaptability' },
{ key: 'endurance', label: 'Endurance' }];


const COLORS = [
'#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4',
'#ef4444', '#84cc16', '#f97316', '#14b8a6'];


export default function VisualTeamMap({ officers }) {
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [synthesisAnswers, setSynthesisAnswers] = useState({
    over_indexed: '',
    thin_areas: '',
    invisible_load: '',
    style_clashes: '',
    explicit_norms: ''
  });
  const [operatingNorms, setOperatingNorms] = useState({
    decision_rights: '',
    escalation_paths: '',
    feedback_norms: '',
    intensity_expectations: '',
    recovery_norms: ''
  });
  const [showNorms, setShowNorms] = useState(false);
  const [selectedOfficers, setSelectedOfficers] = useState(new Set());

  // Filter officers with scoring data
  const officersWithScores = officers.filter((o) => o.user_manual?.scoring);

  // Prepare radar chart data
  const radarData = DIMENSIONS.map((dim) => {
    const dataPoint = { dimension: dim.label };
    officersWithScores.forEach((officer, idx) => {
      dataPoint[officer.callsign] = officer.user_manual?.scoring?.[dim.key] || 0;
    });
    return dataPoint;
  });

  // Detect patterns
  const detectPatterns = () => {
    const patterns = [];

    // Burnout risk: low endurance + high intensity
    const burnoutRisk = officersWithScores.filter((o) => {
      const scores = o.user_manual?.scoring || {};
      return scores.endurance <= 2 && scores.intensity >= 4;
    });
    if (burnoutRisk.length > 0) {
      patterns.push({
        type: 'danger',
        title: 'Burnout Risk Detected',
        description: `${burnoutRisk.map((o) => o.callsign).join(', ')} - Low endurance + High intensity`,
        officers: burnoutRisk
      });
    }

    // Chaos risk: high speed + low ownership
    const chaosRisk = officersWithScores.filter((o) => {
      const scores = o.user_manual?.scoring || {};
      return scores.speed >= 4 && scores.ownership <= 2;
    });
    if (chaosRisk.length > 0) {
      patterns.push({
        type: 'warning',
        title: 'Chaos Risk',
        description: `${chaosRisk.map((o) => o.callsign).join(', ')} - High speed + Low ownership`,
        officers: chaosRisk
      });
    }

    // Directness mismatch
    const directnessScores = officersWithScores.map((o) => o.user_manual?.scoring?.directness || 0);
    const maxDirectness = Math.max(...directnessScores);
    const minDirectness = Math.min(...directnessScores);
    if (maxDirectness - minDirectness >= 3) {
      patterns.push({
        type: 'warning',
        title: 'Directness Mismatch',
        description: 'Large gap in directness scores may lead to communication conflicts',
        officers: []
      });
    }

    // Similarity check
    const avgScores = DIMENSIONS.map((dim) => {
      const scores = officersWithScores.map((o) => o.user_manual?.scoring?.[dim.key] || 0);
      const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
      const variance = scores.reduce((sum, score) => sum + Math.pow(score - avg, 2), 0) / scores.length;
      return { dimension: dim.label, variance };
    });
    const lowVarianceDims = avgScores.filter((s) => s.variance < 0.5);
    if (lowVarianceDims.length >= 3) {
      patterns.push({
        type: 'info',
        title: 'Potential Blind Spots',
        description: `Team shows high similarity in: ${lowVarianceDims.map((d) => d.dimension).join(', ')}`,
        officers: []
      });
    }

    return patterns;
  };

  const patterns = detectPatterns();

  const handleLegendClick = (callsign) => {
    const newSelected = new Set(selectedOfficers);
    if (newSelected.has(callsign)) {
      newSelected.delete(callsign);
    } else {
      newSelected.add(callsign);
    }
    setSelectedOfficers(newSelected);
  };

  const getRadarColor = (callsign, colorIndex) => {
    if (selectedOfficers.size === 0) {
      return COLORS[colorIndex % COLORS.length];
    }
    return selectedOfficers.has(callsign) ? COLORS[colorIndex % COLORS.length] : '#cbd5e1';
  };

  const handleAiSynthesis = async () => {
    setAiLoading(true);
    try {
      const teamData = officersWithScores.map((o) => ({
        callsign: o.callsign,
        role: o.custom_role,
        scores: o.user_manual?.scoring || {}
      }));

      const prompt = `You are a leadership advisor helping a commander synthesize their team's working style data.

Team Scoring Data (1-5 scale):
${JSON.stringify(teamData, null, 2)}

Current Synthesis Answers:
${JSON.stringify(synthesisAnswers, null, 2)}

Detected Patterns:
${patterns.map((p) => `- ${p.title}: ${p.description}`).join('\n')}

Based on the team data and patterns, provide thoughtful, specific answers to help the commander understand their team dynamics. Then suggest concrete operating norms.

Return JSON with two sections:
{
  "synthesis": {
    "over_indexed": "What strengths or behaviors the team has in abundance",
    "thin_areas": "Where the team lacks capability or coverage",
    "invisible_load": "Who might be carrying more than visible",
    "style_clashes": "Potential communication/work style conflicts",
    "explicit_norms": "What norms should be set proactively"
  },
  "norms": {
    "decision_rights": "Clear decision-making framework",
    "escalation_paths": "When and how to escalate",
    "feedback_norms": "How feedback should be given/received",
    "intensity_expectations": "What intensity is expected/sustainable",
    "recovery_norms": "How to maintain energy and prevent burnout"
  }
}`;

      const result = await base44.integrations.Core.InvokeLLM({
        prompt,
        response_json_schema: {
          type: 'object',
          properties: {
            synthesis: {
              type: 'object',
              properties: {
                over_indexed: { type: 'string' },
                thin_areas: { type: 'string' },
                invisible_load: { type: 'string' },
                style_clashes: { type: 'string' },
                explicit_norms: { type: 'string' }
              }
            },
            norms: {
              type: 'object',
              properties: {
                decision_rights: { type: 'string' },
                escalation_paths: { type: 'string' },
                feedback_norms: { type: 'string' },
                intensity_expectations: { type: 'string' },
                recovery_norms: { type: 'string' }
              }
            }
          }
        }
      });

      setSynthesisAnswers(result.synthesis);
      setOperatingNorms(result.norms);
      setShowNorms(true);
    } catch (error) {
      console.error('AI synthesis failed:', error);
    } finally {
      setAiLoading(false);
      setAiDialogOpen(false);
    }
  };

  if (officersWithScores.length === 0) {
    return (
      <Card className="glass-panel border-none">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-cyan-400" />
            Visual Team Map
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12 text-slate-400">
            <Users className="w-16 h-16 mx-auto mb-4 text-slate-600" />
            <p className="text-lg mb-2">No team scoring data yet</p>
            <p className="text-sm text-slate-500">
              Officers need to complete the self-scoring section in their Personal User Manual
            </p>
          </div>
        </CardContent>
      </Card>);

  }

  return (
    <>
      <Card className="bg-card text-slate-600 rounded-xl border shadow glass-panel border-none">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-slate-600 font-semibold tracking-tight leading-none flex items-center gap-2">
                <Users className="w-5 h-5 text-cyan-400" />
                Visual Team Map
              </CardTitle>
              <p className="text-xs text-slate-400 mt-1">
                Team dynamics across {officersWithScores.length} officers
              </p>
            </div>
            <Button
              onClick={() => setAiDialogOpen(true)}
              className="bg-purple-600 hover:bg-purple-700">

              <Sparkles className="w-4 h-4 mr-2" />
              AI Synthesis
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Radar Chart */}
          <div className="bg-slate-50 p-6 rounded-xl border border-slate-800">
            <ResponsiveContainer width="100%" height={400}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#475569" />
                <PolarAngleAxis dataKey="dimension" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fill: '#64748b' }} />
                {officersWithScores.map((officer, idx) => {
                  const color = getRadarColor(officer.callsign, idx);
                  const isMonochrome = color === '#cbd5e1';
                  return (
                    <Radar
                      key={officer.id}
                      name={officer.callsign}
                      dataKey={officer.callsign}
                      stroke={color}
                      fill={color}
                      fillOpacity={isMonochrome ? 0.1 : 0.3}
                      strokeWidth={2} />
                  );
                })}
                <Legend 
                  wrapperStyle={{ paddingTop: '20px', cursor: 'pointer' }}
                  onClick={(e) => handleLegendClick(e.dataKey)}
                />
              </RadarChart>
            </ResponsiveContainer>
            {selectedOfficers.size > 0 && (
              <div className="text-xs text-slate-500 mt-3 text-center">
                Click legend names to toggle highlight. {selectedOfficers.size} officer(s) selected.
              </div>
            )}
          </div>

          {/* Pattern Detection */}
          {patterns.length > 0 &&
          <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">
                Pattern Detection
              </h3>
              {patterns.map((pattern, idx) =>
            <div
              key={idx}
              className={`p-4 rounded-lg border ${
              pattern.type === 'danger' ?
              'bg-red-500/10 border-red-500/30' :
              pattern.type === 'warning' ?
              'bg-amber-500/10 border-amber-500/30' :
              'bg-blue-500/10 border-blue-500/30'}`
              }>

                  <div className="flex items-start gap-3">
                    {pattern.type === 'danger' ?
                <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" /> :
                pattern.type === 'warning' ?
                <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" /> :

                <TrendingUp className="w-5 h-5 text-blue-400 mt-0.5" />
                }
                    <div className="flex-1">
                      <div className="text-slate-600 mb-1 font-semibold">{pattern.title}</div>
                      <div className="text-slate-600 text-sm">{pattern.description}</div>
                    </div>
                  </div>
                </div>
            )}
            </div>
          }

          {/* User Manuals Table */}
          <UserManualTable officers={officers} />

          {/* Operating Norms Output */}
          {showNorms &&
          <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">
                  Operating Norms
                </h3>
                <Badge className="bg-emerald-500/20 text-emerald-400 border-none">
                  AI Generated
                </Badge>
              </div>

              {/* Synthesis Insights */}
              <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800 space-y-3">
                <h4 className="text-sm font-semibold text-white">Team Synthesis</h4>
                {Object.entries(synthesisAnswers).map(([key, value]) =>
              <div key={key}>
                    <div className="text-xs text-slate-500 font-semibold mb-1 uppercase">
                      {key.replace(/_/g, ' ')}
                    </div>
                    <div className="text-sm text-slate-300">{value}</div>
                  </div>
              )}
              </div>

              {/* Defined Norms */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(operatingNorms).map(([key, value]) =>
              <div key={key} className="bg-indigo-500/10 rounded-lg p-4 border border-indigo-500/30">
                    <div className="text-sm font-semibold text-indigo-400 mb-2">
                      {key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </div>
                    <div className="text-sm text-slate-300">{value}</div>
                  </div>
              )}
              </div>
            </div>
          }
        </CardContent>
      </Card>

      {/* AI Synthesis Dialog */}
      <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
        <DialogContent data-testid="dash-visualteammap-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-400" />
              Team Synthesis & Operating Norms
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-400">
              The AI will analyze your team's scoring patterns and help you identify key insights and define operating norms.
            </p>

            <div className="bg-slate-950 border border-slate-800 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-slate-300 mb-3">Analysis will cover:</h4>
              <ul className="text-xs text-slate-400 space-y-2">
                <li>• Where your team is over-indexed (abundant strengths)</li>
                <li>• Where you're dangerously thin (capability gaps)</li>
                <li>• Who might be carrying invisible load</li>
                <li>• Potential communication style clashes</li>
                <li>• Explicit norms to set proactively</li>
              </ul>
            </div>

            <div className="bg-slate-950 border border-slate-800 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-slate-300 mb-3">Will generate norms for:</h4>
              <ul className="text-xs text-slate-400 space-y-2">
                <li>• Decision rights framework</li>
                <li>• Escalation paths</li>
                <li>• Feedback norms</li>
                <li>• Intensity expectations</li>
                <li>• Recovery norms</li>
              </ul>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setAiDialogOpen(false)}
                disabled={aiLoading}>

                Cancel
              </Button>
              <Button
                onClick={handleAiSynthesis}
                disabled={aiLoading}
                className="bg-purple-600 hover:bg-purple-700">

                {aiLoading ?
                <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </> :

                <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Synthesis
                  </>
                }
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>);

}